import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../utils/formatDate';
import { exportCSV } from '../utils/exportCSV';
import { CALL_STATUS } from '../utils/constants';

const CallLogs = () => {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [mentorFilter, setMentorFilter] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [notesSearch, setNotesSearch] = useState('');
  const [mentorsList, setMentorsList] = useState([]);
  const [studentsList, setStudentsList] = useState([]);
  
  const [stats, setStats] = useState({
    connectedToday: 0,
    connectedThisWeek: 0,
    totalToday: 0,
    totalThisWeek: 0,
    connectionRate: 0,
    unengaged: 0,
    atRisk: 0
  });
  const [unengagedStudentIds, setUnengagedStudentIds] = useState([]);
  const [atRiskStudentIds, setAtRiskStudentIds] = useState([]);
  const [activeStatFilter, setActiveStatFilter] = useState(null);

  useEffect(() => {
    const fetchFilters = async () => {
      const { data: mentors } = await supabase
        .from('mentors')
        .select('id, full_name, email')
        .order('full_name');
      if (mentors) setMentorsList(mentors);

      const { data: students } = await supabase
        .from('students')
        .select('id, name, unique_id')
        .order('name')
        .limit(100);
      if (students) setStudentsList(students);
    };

    if (role === 'admin') fetchFilters();
  }, [role]);

  useEffect(() => {
    if (role === 'admin') {
      fetchCallStats();
    }
  }, [role]);

  const fetchCallStats = async () => {
    const now = new Date();
    const localMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const localEndOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const todayStartUTC = localMidnight.toISOString();
    const todayEndUTC = localEndOfDay.toISOString();

    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);

    // Use server-side counts (no row limit) for call stats
    const [
      { count: connectedToday },
      { count: connectedWeek },
      { count: totalToday },
      { count: totalWeek },
      { data: allStudents }
    ] = await Promise.all([
      supabase.from('calls').select('*', { count: 'exact', head: true }).eq('call_status', CALL_STATUS.CONNECTED).gte('created_at', todayStartUTC).lte('created_at', todayEndUTC),
      supabase.from('calls').select('*', { count: 'exact', head: true }).eq('call_status', CALL_STATUS.CONNECTED).gte('created_at', weekAgo.toISOString()),
      supabase.from('calls').select('*', { count: 'exact', head: true }).gte('created_at', todayStartUTC).lte('created_at', todayEndUTC),
      supabase.from('calls').select('*', { count: 'exact', head: true }).gte('created_at', weekAgo.toISOString()),
      supabase.from('students').select('id, mentor_id')
    ]);

    // Fetch all calls with only student_id + created_at, paginating past the 1000 row limit
    let allCalls = [];
    let from = 0;
    const batchSize = 1000;
    while (true) {
      const { data: batch, error } = await supabase
        .from('calls')
        .select('student_id, created_at')
        .order('created_at', { ascending: false })
        .range(from, from + batchSize - 1);
      if (error || !batch || batch.length === 0) break;
      allCalls = allCalls.concat(batch);
      if (batch.length < batchSize) break;
      from += batchSize;
    }

    // Build map of student_id → latest call date
    const studentLastCall = {};
    allCalls.forEach(c => {
      if (c.student_id) {
        if (!studentLastCall[c.student_id] || c.created_at > studentLastCall[c.student_id]) {
          studentLastCall[c.student_id] = c.created_at;
        }
      }
    });

    const allStudentData = allStudents || [];
    const nowMs = now.getTime();
    const fourteenDaysAgoMs = nowMs - 14 * 24 * 60 * 60 * 1000;
    const twentyEightDaysAgoMs = nowMs - 28 * 24 * 60 * 60 * 1000;

    const unengagedIds = [];
    const atRiskIds = [];

    allStudentData.forEach(s => {
      if (!s.mentor_id) return;
      const lastCall = studentLastCall[s.id];
      if (!lastCall) return;
      const lastCallMs = new Date(lastCall).getTime();
      if (lastCallMs < twentyEightDaysAgoMs) {
        atRiskIds.push(s.id);
        unengagedIds.push(s.id); // at-risk is a subset of unengaged
      } else if (lastCallMs < fourteenDaysAgoMs) {
        unengagedIds.push(s.id);
      }
    });

    setUnengagedStudentIds(unengagedIds);
    setAtRiskStudentIds(atRiskIds);

    setStats({
      connectedToday: connectedToday || 0,
      connectedThisWeek: connectedWeek || 0,
      totalToday: totalToday || 0,
      totalThisWeek: totalWeek || 0,
      connectionRate: totalWeek > 0 ? Math.round(((connectedWeek || 0) / totalWeek) * 100) : 0,
      unengaged: unengagedIds.length,
      atRisk: atRiskIds.length
    });
  };

  useEffect(() => {
    setPage(0);
  }, [dateFrom, dateTo, mentorFilter, studentFilter, statusFilter, notesSearch, activeStatFilter]);

  const fetchCallLogs = useCallback(async () => {
    try {
      setLoading(true);

      const isEngagementFilter = activeStatFilter === 'unengaged' || activeStatFilter === 'atRisk';
      const engagementIds = activeStatFilter === 'atRisk' ? atRiskStudentIds : activeStatFilter === 'unengaged' ? unengagedStudentIds : [];

      if (isEngagementFilter && engagementIds.length === 0) {
        setCallLogs([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      // Build base query — no .range() for engagement filters (client-side pagination)
      let query = supabase
        .from('calls')
        .select(`
          *,
          students (name, unique_id),
          mentors (full_name, email)
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      if (!isEngagementFilter) {
        query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
      }

      if (role === 'mentor') {
        query = query.eq('mentor_id', user.id);
      } else if (mentorFilter) {
        query = query.eq('mentor_id', mentorFilter);
      }

      if (dateFrom) {
        const [fy, fm, fd] = dateFrom.split('-').map(Number);
        const fromLocal = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
        query = query.gte('created_at', fromLocal.toISOString());
      }

      if (dateTo) {
        const [ty, tm, td] = dateTo.split('-').map(Number);
        const toLocalEnd = new Date(ty, tm - 1, td + 1, 0, 0, 0, 0);
        query = query.lt('created_at', toLocalEnd.toISOString());
      }

      if (studentFilter) {
        query = query.eq('student_id', studentFilter);
      }

      if (statusFilter) {
        query = query.eq('call_status', statusFilter);
      }

      if (activeStatFilter === 'connectedToday') {
        const now = new Date();
        const localMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        query = query.eq('call_status', CALL_STATUS.CONNECTED).gte('created_at', localMidnight.toISOString());
      } else if (activeStatFilter === 'connectedThisWeek') {
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        query = query.eq('call_status', CALL_STATUS.CONNECTED).gte('created_at', weekAgo.toISOString());
      }

      if (notesSearch) {
        query = query.ilike('notes', `%${notesSearch}%`);
      }

      // For engagement filters, fetch a larger batch and filter client-side
      // because .in() with many UUIDs can exceed URL length limits
      if (isEngagementFilter) {
        query = query.limit(5000);

        let { data, error } = await query;
        if (error) throw error;

        const engagementIdSet = new Set(engagementIds);
        const matchingLogs = (data || []).filter(log => engagementIdSet.has(log.student_id));

        // Keep only the latest call per student so count matches the stat card
        const latestByStudent = {};
        matchingLogs.forEach(log => {
          if (!latestByStudent[log.student_id] || log.created_at > latestByStudent[log.student_id].created_at) {
            latestByStudent[log.student_id] = log;
          }
        });
        const filtered = Object.values(latestByStudent).sort((a, b) => b.created_at.localeCompare(a.created_at));

        const start = page * PAGE_SIZE;
        const paged = filtered.slice(start, start + PAGE_SIZE);

        const transformedData = paged.map(log => ({
          ...log,
          student: log.students || { name: 'Unknown Student', unique_id: 'N/A' },
          mentor: log.mentors || { full_name: 'Unknown Mentor', email: 'N/A' }
        }));

        setCallLogs(transformedData);
        setTotalCount(filtered.length);
        return;
      }

      let { data, count, error } = await query;
      if (error) throw error;

      const transformedData = (data || []).map(log => ({
        ...log,
        student: log.students || { name: 'Unknown Student', unique_id: 'N/A' },
        mentor: log.mentors || { full_name: 'Unknown Mentor', email: 'N/A' }
      }));

      setCallLogs(transformedData);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching call logs:', error);
      setCallLogs([]);
    } finally {
      setLoading(false);
    }
  }, [role, user?.id, page, dateFrom, dateTo, mentorFilter, studentFilter, statusFilter, notesSearch, activeStatFilter, unengagedStudentIds, atRiskStudentIds]);

  useEffect(() => {
    if (user && role) fetchCallLogs();
  }, [fetchCallLogs, user, role]);

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setMentorFilter('');
    setStudentFilter('');
    setStatusFilter('');
    setNotesSearch('');
    setActiveStatFilter(null);
  };

  const handleExport = () => {
    const headers = ['Student', 'EPS ID', 'Mentor', 'Date', 'Status', 'Notes'];
    const rows = callLogs.map(log => [
      log.student?.name || 'Unknown',
      log.student?.unique_id || 'N/A',
      log.mentor?.full_name || 'Unknown',
      formatDate(log.created_at),
      log.call_status === CALL_STATUS.CONNECTED ? 'Connected' : 'Not Connected',
      log.notes || ''
    ]);
    exportCSV(headers, rows, 'call_logs');
  };

  const activeFilters = [
    { key: 'statFilter', label: activeStatFilter === 'unengaged' ? 'Unengaged (14 days)' : activeStatFilter === 'atRisk' ? 'At-Risk (28 days)' : activeStatFilter === 'connectedToday' ? 'Connected Today' : activeStatFilter === 'connectedThisWeek' ? 'Connected This Week' : '', value: activeStatFilter },
    { key: 'dateFrom', label: `From: ${dateFrom}`, value: dateFrom },
    { key: 'dateTo', label: `To: ${dateTo}`, value: dateTo },
    { key: 'mentor', label: mentorsList.find(m => m.id === mentorFilter)?.full_name || 'Mentor', value: mentorFilter },
    { key: 'student', label: studentsList.find(s => s.id === studentFilter)?.name || 'Student', value: studentFilter },
    { key: 'status', label: statusFilter === CALL_STATUS.CONNECTED ? 'Connected' : 'Not Connected', value: statusFilter },
  ].filter(f => f.value);

  const removeFilter = (key) => {
    if (key === 'statFilter') setActiveStatFilter(null);
    else if (key === 'dateFrom') setDateFrom('');
    else if (key === 'dateTo') setDateTo('');
    else if (key === 'mentor') setMentorFilter('');
    else if (key === 'student') setStudentFilter('');
    else if (key === 'status') setStatusFilter('');
  };

  if (loading && callLogs.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="space-y-2 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Call Logs</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Review and track all mentor-student call sessions.
            </p>
          </div>
          <button onClick={handleExport} className="btn btn-secondary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
      </header>

      {role === 'admin' && (
        <section className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 animate-fade-in">
          <div 
            onClick={() => setActiveStatFilter(activeStatFilter === 'connectedToday' ? null : 'connectedToday')}
            className={`card p-4 cursor-pointer transition-all hover:shadow-md ${activeStatFilter === 'connectedToday' ? 'ring-2 ring-brand' : ''}`}
          >
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Connected Today</p>
            <p className="text-2xl font-semibold text-brand mt-1">{stats.connectedToday}</p>
          </div>
          <div 
            onClick={() => setActiveStatFilter(activeStatFilter === 'connectedThisWeek' ? null : 'connectedThisWeek')}
            className={`card p-4 cursor-pointer transition-all hover:shadow-md ${activeStatFilter === 'connectedThisWeek' ? 'ring-2 ring-brand' : ''}`}
          >
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Connected This Week</p>
            <p className="text-2xl font-semibold text-brand mt-1">{stats.connectedThisWeek}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Total Today</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-1">{stats.totalToday}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Total This Week</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-1">{stats.totalThisWeek}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Connection Rate</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-1">{stats.connectionRate}%</p>
          </div>
          <div 
            onClick={() => setActiveStatFilter(activeStatFilter === 'unengaged' ? null : 'unengaged')}
            className={`card p-4 cursor-pointer transition-all hover:shadow-md ${activeStatFilter === 'unengaged' ? 'ring-2 ring-amber-500' : ''}`}
          >
            <p className="text-xs uppercase tracking-wide text-amber-600 dark:text-amber-400">Unengaged (14 days)</p>
            <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400 mt-1">{stats.unengaged}</p>
          </div>
          <div 
            onClick={() => setActiveStatFilter(activeStatFilter === 'atRisk' ? null : 'atRisk')}
            className={`card p-4 cursor-pointer transition-all hover:shadow-md ${activeStatFilter === 'atRisk' ? 'ring-2 ring-red-500' : ''}`}
          >
            <p className="text-xs uppercase tracking-wide text-red-600 dark:text-red-400">At-Risk (28 days)</p>
            <p className="text-2xl font-semibold text-red-600 dark:text-red-400 mt-1">{stats.atRisk}</p>
          </div>
        </section>
      )}

      <section className="card animate-fade-in">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Filters</h3>
        
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="date-from" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Date From
            </label>
            <input
              type="date"
              id="date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label htmlFor="date-to" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Date To
            </label>
            <input
              type="date"
              id="date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label htmlFor="mentor-filter" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Mentor
            </label>
            <select
              id="mentor-filter"
              value={mentorFilter}
              onChange={(e) => setMentorFilter(e.target.value)}
              className="input select"
            >
              <option value="">All Mentors</option>
              {mentorsList.map((mentor) => (
                <option key={mentor.id} value={mentor.id}>
                  {mentor.full_name} ({mentor.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="student-filter" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Student
            </label>
            <select
              id="student-filter"
              value={studentFilter}
              onChange={(e) => setStudentFilter(e.target.value)}
              className="input select"
            >
              <option value="">All Students</option>
              {studentsList.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.name} ({student.unique_id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Status
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input select"
            >
              <option value="">All Statuses</option>
              <option value="connected">Connected</option>
              <option value="not_connected">Not Connected</option>
            </select>
          </div>

          <div>
            <label htmlFor="notes-search" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Search Notes
            </label>
            <input
              type="text"
              id="notes-search"
              value={notesSearch}
              onChange={(e) => setNotesSearch(e.target.value)}
              placeholder="Search in notes..."
              className="input"
            />
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="mt-4 filter-chips">
            {activeFilters.map(filter => (
              <span key={filter.key} className="chip active">
                {filter.label}
                <button onClick={() => removeFilter(filter.key)} className="chip-remove">×</button>
              </span>
            ))}
            <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
              Clear all
            </button>
          </div>
        )}
      </section>

      <section className="card animate-fade-in">
        {callLogs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📞</div>
            <h3 className="empty-state-title">No call logs found</h3>
            <p className="empty-state-description">
              {activeFilters.length > 0
                ? 'Try adjusting your filters to see more results.'
                : 'No calls have been logged yet.'}
            </p>
            {activeFilters.length > 0 && (
              <button onClick={clearFilters} className="btn btn-secondary mt-4">Clear filters</button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile: Card layout */}
            <div className="md:hidden space-y-3">
              {callLogs.map((log) => (
                <div
                  key={log.id}
                  onClick={() => navigate(`/students/${log.student_id}`)}
                  className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer hover:border-brand/30 active:scale-[0.99] transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 dark:text-white text-sm truncate">{log.student?.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{log.mentor?.full_name} · {formatDate(log.created_at)}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                      log.call_status === CALL_STATUS.CONNECTED
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {log.call_status === CALL_STATUS.CONNECTED ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                  {log.notes && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 line-clamp-2">{log.notes}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/50">
              <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                <thead className="bg-slate-100/60 dark:bg-slate-800/60 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Student</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">EPS ID</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Mentor</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Date</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Status</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {callLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="cursor-pointer hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 dark:hover:bg-slate-900/40"
                      onClick={() => navigate(`/students/${log.student_id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 dark:text-white">{log.student?.name}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{log.student?.unique_id}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-900 dark:text-white">{log.mentor?.full_name}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{log.mentor?.email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          log.call_status === CALL_STATUS.CONNECTED
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}>
                          {log.call_status === CALL_STATUS.CONNECTED ? 'Connected' : 'Not Connected'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-xs">
                        <div className="line-clamp-2">{log.notes || '—'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount} results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * PAGE_SIZE >= totalCount}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CallLogs;